import { useState, useEffect } from 'react'
import { Zap, Wrench, Rocket } from 'lucide-react'
import { fetchLivePatchNotes } from '@/lib/queries/admin'

const StarEmoticon = ({ className }) => (
  <img src="/icons/star.png" alt="" className={`${className || ''} scale-125`} />
)

const CATEGORY_CONFIG = {
  new_features: { label: 'New Features', icon: StarEmoticon, color: 'text-[var(--gold)]' },
  improvements: { label: 'Improvements', icon: Zap, color: 'text-blue-400' },
  bug_fixes: { label: 'Bug Fixes', icon: Wrench, color: 'text-green-400' },
  coming_soon: { label: 'Coming Soon', icon: Rocket, color: 'text-purple-400' },
}

const CATEGORY_ORDER = ['new_features', 'improvements', 'bug_fixes', 'coming_soon']

export default function ChangelogPage() {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    fetchLivePatchNotes()
      .then(data => {
        setNotes(data)
        setLoadError(false)
        setLoading(false)
      })
      .catch(() => {
        setLoadError(true)
        setLoading(false)
      })
  }, [])

  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Page header */}
        <div className="text-center mb-14">
          <p className="text-[var(--gold)] text-sm font-semibold uppercase tracking-widest mb-3">
            Release Notes
          </p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
            Changelog
          </h1>
          <p className="text-[var(--text-muted)] text-lg">
            Every update, improvement, and fix — documented with full transparency.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-[var(--text-muted)] py-12">Loading...</div>
        ) : loadError ? (
          <div className="text-center text-[var(--text-muted)] py-12">
            We couldn't load the changelog right now. Please refresh in a moment.
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center text-[var(--text-muted)] py-12">
            No public releases yet. Check back soon.
          </div>
        ) : (
          <div className="space-y-12">
            {notes.map((note, idx) => (
              <div key={note.id} className="relative pl-8">
                {/* Timeline line */}
                {idx < notes.length - 1 && (
                  <div className="absolute left-3 top-8 bottom-[-3rem] w-px bg-[var(--glass-border)]" />
                )}
                {/* Timeline dot */}
                <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-[var(--gold)]/20 border-2 border-[var(--gold)] flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[var(--gold)]" />
                </div>

                {/* Version + title */}
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <span className="font-heading font-bold text-[var(--text-primary)] text-xl">
                    v{note.version}
                  </span>
                  <span className="text-[var(--text-muted)]">—</span>
                  <span className="text-[var(--text-primary)] font-medium">{note.title}</span>
                </div>
                {note.released_at && (
                  <p className="text-[var(--text-muted)] text-xs mb-5">
                    {new Date(note.released_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                )}

                {/* Categories */}
                <div className="space-y-5">
                  {CATEGORY_ORDER.map(cat => {
                    const cfg = CATEGORY_CONFIG[cat]
                    const items = note.patch_note_items
                      .filter(i => i.category === cat)
                      .sort((a, b) => a.sort_order - b.sort_order)
                    if (!items.length) return null
                    const Icon = cfg.icon
                    return (
                      <div key={cat}>
                        <div className={`flex items-center gap-2 mb-2 ${cfg.color}`}>
                          <Icon className="w-4 h-4" />
                          <span className="text-xs font-semibold uppercase tracking-wider">
                            {cfg.label}
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {items.map(item => (
                            <li
                              key={item.id}
                              className="flex items-start gap-2 text-sm text-[var(--text-muted)]"
                            >
                              <span className="text-[var(--gold)] mt-1.5 text-xs leading-none">
                                ◆
                              </span>
                              {item.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
